import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearStaffIdleLockStorage,
  loadStaffIdleLockState,
  persistStaffIdleLockState,
  resolveStaffIdleLockMsForUser,
  shouldApplyStaffIdleLock,
} from "../helpers/staffIdleLock";

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

/**
 * Tracks inactivity for team accounts. When enabled and idle past the configured
 * timeout, `locked` becomes true until unlock() or clear on logout.
 */
export default function useStaffIdleLock({ user, enterprise, enabled }) {
  const uid = user?.uid;
  const applies = enabled && shouldApplyStaffIdleLock(user, enterprise);
  const timeoutMs = resolveStaffIdleLockMsForUser(user, enterprise);
  const timeoutMsRef = useRef(timeoutMs);
  timeoutMsRef.current = timeoutMs;

  const [locked, setLocked] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const lockedRef = useRef(false);
  const timerRef = useRef(null);

  const syncLocked = useCallback((next) => {
    lockedRef.current = next;
    setLocked(next);
  }, []);

  const scheduleCheck = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!applies || lockedRef.current) return;

    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = Math.max(0, timeoutMsRef.current - elapsed);
    timerRef.current = setTimeout(() => {
      if (!applies || lockedRef.current) return;
      if (Date.now() - lastActivityRef.current >= timeoutMsRef.current) {
        syncLocked(true);
        persistStaffIdleLockState({
          uid,
          locked: true,
          lastActivityAt: lastActivityRef.current,
        });
      } else {
        scheduleCheck();
      }
    }, remaining);
  }, [applies, syncLocked, uid]);

  const noteActivity = useCallback(() => {
    if (!applies || lockedRef.current) return;
    const now = Date.now();
    lastActivityRef.current = now;
    persistStaffIdleLockState({
      uid,
      locked: false,
      lastActivityAt: now,
    });
    scheduleCheck();
  }, [applies, scheduleCheck, uid]);

  const unlock = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    syncLocked(false);
    persistStaffIdleLockState({
      uid,
      locked: false,
      lastActivityAt: now,
    });
    scheduleCheck();
  }, [scheduleCheck, syncLocked, uid]);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    clearStaffIdleLockStorage();
    syncLocked(false);
  }, [syncLocked]);

  // Bootstrap / user change / timeout change
  useEffect(() => {
    if (!applies || !uid) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      syncLocked(false);
      return undefined;
    }

    const state = loadStaffIdleLockState(uid, timeoutMs);
    lastActivityRef.current = state.lastActivityAt;
    syncLocked(state.locked);
    if (!state.locked) scheduleCheck();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [applies, uid, timeoutMs, scheduleCheck, syncLocked]);

  // Activity listeners
  useEffect(() => {
    if (!applies || locked) return undefined;

    let throttleUntil = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now < throttleUntil) return;
      throttleUntil = now + 1000;
      noteActivity();
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    );
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (Date.now() - lastActivityRef.current >= timeoutMsRef.current) {
          syncLocked(true);
          persistStaffIdleLockState({
            uid,
            locked: true,
            lastActivityAt: lastActivityRef.current,
          });
        } else {
          noteActivity();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, onActivity)
      );
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [applies, locked, noteActivity, syncLocked, uid]);

  return {
    locked: applies && locked,
    unlock,
    clear,
  };
}
